import { useCallback } from 'react';
import posthog from 'posthog-js';
import { PALETTE, TRACKER_MOOD_LEVELS } from '../lib/constants';
import { uid, sanitize, lbsToKg, localDateStr, freqChanged, nextFreqHistory } from '../lib/utils';
import { supportsBlend, normalizeBlend, toBlendRows } from '../lib/blend';

export function useStoredActions({ stored, upd, user, getCmpd, showToast, cmpdForm, setAC, setEC, setCF, setPendingConfirm, setCheckIn, setPostLogCheckIn, setUserWeight, weightUnit, bloodGlucoseUnit, setResumeNudge }) {

  const saveCmpd = useCallback(() => {
    const isNew = !cmpdForm.id;
    const key = isNew ? (cmpdForm.label || "c").toLowerCase().replace(/[^a-z0-9]/g, "_") + "_" + uid().slice(0, 8) : cmpdForm.id;
    const pal = PALETTE.find(p => p.color === (cmpdForm.color || PALETTE[2].color)) || PALETTE[2];
    const existing = isNew ? null : getCmpd(key);
    const newFreq = cmpdForm.freq || [];
    const changed = !isNew && freqChanged(existing.freq, newFreq);
    // freqHistory accumulates every past schedule (see nextFreqHistory) instead of
    // remembering only the single most recent one, so a later edit can never silently
    // discard an earlier schedule change the way overwriting one prevFreq slot did.
    const freqHistory = isNew
      ? [{ freq: newFreq, from: localDateStr() }]
      : (changed ? nextFreqHistory(existing, newFreq) : existing.freqHistory);
    // deliveryType is locked after creation (CompoundForm disables the selectors on edit too):
    // shot history is tagged with the delivery type it was actually logged under, and the
    // injection/pill streak split reads a compound's CURRENT deliveryType, so changing it
    // post-creation would silently move that whole history between the two streak counters.
    const deliveryType = isNew ? (cmpdForm.deliveryType || 'syringe') : existing.deliveryType;
    // saveCmpd rebuilds the compound from a field whitelist — blend/bacMl must be listed explicitly.
    const blend = supportsBlend(deliveryType) ? normalizeBlend(cmpdForm.blend) : undefined;
    const bacMl = blend ? (parseFloat(cmpdForm.bacMl) || undefined) : undefined;
    const c = { label: sanitize(cmpdForm.label, 100), shortLabel: sanitize(cmpdForm.shortLabel, 50), goal: cmpdForm.goal, color: pal.color, dim: pal.dim, text: pal.text, protocols: isNew ? {} : existing.protocols, syringeML: parseFloat(cmpdForm.syringeML) || 1.0, freq: newFreq, ...(freqHistory !== undefined ? { freqHistory } : {}), notes: sanitize(cmpdForm.notes || "", 500), builtIn: false, deliveryType, glp: !!cmpdForm.glp, ...(cmpdForm.titration ? { titration: cmpdForm.titration } : {}), ...(cmpdForm.reorderDays ? { reorderDays: parseInt(cmpdForm.reorderDays) } : {}), ...(existing?.cycleBreaks ? { cycleBreaks: existing.cycleBreaks } : {}), ...(deliveryType === 'pill' && cmpdForm.timeOfDay ? { timeOfDay: cmpdForm.timeOfDay } : {}), ...(blend ? { blend } : {}), ...(bacMl ? { bacMl } : {}) };
    const compounds = { ...stored.compounds, [key]: c };
    const counters = { ...stored.counters };
    if (!(key in counters)) counters[key] = 0;
    const vials = { ...stored.vials };
    if (!(key in vials)) vials[key] = [];
    upd({ compounds, counters, vials });
    setAC(false); setEC(null); setCF({});
    showToast(isNew ? "Added to stack" : "Updated");
  }, [stored, cmpdForm, getCmpd, upd, setAC, setEC, setCF, showToast]);

  const deleteCmpd = useCallback(key => setPendingConfirm({
    message: "Remove this stack item? This cannot be undone.",
    onConfirm: () => {
      const compounds = { ...stored.compounds };
      delete compounds[key];
      const vials = { ...stored.vials };
      delete vials[key];
      const counters = { ...stored.counters };
      delete counters[key];
      upd({ compounds, vials, counters });
    }
  }), [stored, upd, setPendingConfirm]);

  const archiveCmpd = useCallback(key => {
    const compounds = { ...stored.compounds };
    compounds[key] = { ...compounds[key], archived: true, active: false };
    upd({ compounds });
    showToast("Stack item archived");
  }, [stored, upd, showToast]);

  const startBreak = useCallback(key => {
    const compounds = { ...stored.compounds };
    const c = compounds[key];
    compounds[key] = { ...c, cycleBreaks: [...(c.cycleBreaks || []), { from: localDateStr(), to: null }] };
    upd({ compounds });
    showToast('Break started');
  }, [stored, upd, showToast]);

  const endBreak = useCallback(key => {
    const compounds = { ...stored.compounds };
    const c = compounds[key];
    const breaks = c.cycleBreaks || [];
    const openIdx = breaks.findIndex(b => b.to == null);
    if (openIdx === -1) return;
    const nextBreaks = [...breaks];
    nextBreaks[openIdx] = { ...nextBreaks[openIdx], to: localDateStr() };
    compounds[key] = { ...c, cycleBreaks: nextBreaks };
    upd({ compounds });
    // A schedule/vial nudge, not a plain toast: whatever was scheduled before the break
    // is what applies again immediately (see freqForDay), so it's easy to fall out of sync
    // with reality (new dose, changed frequency, fresh vial) without a prompt to check.
    setResumeNudge({ key, label: c.label });
  }, [stored, upd, setResumeNudge]);

  const startEditCmpd = useCallback(key => {
    const c = getCmpd(key);
    const pal = PALETTE.find(p => p.color === c.color) || PALETTE[2];
    setCF({ id: key, label: c.label, shortLabel: c.shortLabel, goal: c.goal || "recovery", color: pal.color, syringeML: c.syringeML || 1.0, freq: c.freq || [], notes: c.notes || "", reorderDays: c.reorderDays || '', deliveryType: c.deliveryType || 'syringe', blend: toBlendRows(c.blend), bacMl: c.bacMl ? String(c.bacMl) : '', glp: !!(c.glp || c.titration), titration: c.titration || null, timeOfDay: c.timeOfDay || null });
    setEC(key); setAC(false);
  }, [getCmpd, setCF, setEC, setAC]);

  const toggleZone = useCallback(id => upd({ hiddenZones: stored.hiddenZones.includes(id) ? stored.hiddenZones.filter(z => z !== id) : [...stored.hiddenZones, id] }), [stored, upd]);
  const toggleHiddenCmpd = useCallback(key => upd({ dashHiddenCmpds: (stored.dashHiddenCmpds || []).includes(key) ? (stored.dashHiddenCmpds || []).filter(k => k !== key) : [...(stored.dashHiddenCmpds || []), key] }), [stored, upd]);

  const stepUpTitration = useCallback(key => {
    const cmpd = getCmpd(key);
    const t = cmpd.titration;
    if (!t) return;
    const currentHolds = (t.holds || []).map(h => ({ ...h, step: t.currentStep ?? 0 }));
    upd({ compounds: { ...stored.compounds, [key]: { ...cmpd, titration: { ...t, currentStep: (t.currentStep ?? 0) + 1, holds: [], archivedHolds: [...(t.archivedHolds || []), ...currentHolds] } } } });
    showToast('Stepped up to next dose');
  }, [stored, getCmpd, upd, showToast]);

  const holdTitration = useCallback((key, weeks = 2) => {
    const cmpd = getCmpd(key);
    const t = cmpd.titration;
    if (!t) return;
    const today = new Date().toISOString().split('T')[0];
    upd({ compounds: { ...stored.compounds, [key]: { ...cmpd, titration: { ...t, holds: [...(t.holds || []), { date: today, weeks }] } } } });
    showToast(`Holding for ${weeks} more weeks`);
  }, [stored, getCmpd, upd, showToast]);

  const addCustomZone = useCallback(zone => upd({ customZones: [...(stored.customZones || []), zone] }), [stored, upd]);
  const removeCustomZone = useCallback(id => upd({ customZones: (stored.customZones || []).filter(z => z.id !== id) }), [stored, upd]);

  const updateCheckIn = useCallback(updated => {
    upd({ checkIns: (stored.checkIns || []).map(ci => ci.date === updated.date ? updated : ci) });
    showToast('Check-in updated');
  }, [stored, upd, showToast]);

  const deleteCheckIn = useCallback(date => {
    upd({ checkIns: (stored.checkIns || []).filter(ci => ci.date !== date) });
    showToast('Check-in deleted');
  }, [stored, upd, showToast]);

  const saveCheckIn = useCallback(data => {
    if (!user || !data) return;
    const w = data.weight != null ? parseFloat(data.weight) : null;
    const bg = data.bloodGlucose != null ? parseFloat(data.bloodGlucose) : null;
    const sys = data.bloodPressureSystolic != null ? parseInt(data.bloodPressureSystolic, 10) : null;
    const dia = data.bloodPressureDiastolic != null ? parseInt(data.bloodPressureDiastolic, 10) : null;
    const hr = data.heartRate != null ? parseFloat(data.heartRate) : null;
    const patch = { checkIns: [{ date: data.date, mood: data.mood, symptoms: data.effects, notes: data.note, weight: w, weightUnit: w != null ? weightUnit : undefined, bloodGlucose: bg, bloodGlucoseUnit: bg != null ? bloodGlucoseUnit : undefined, bloodPressureSystolic: sys, bloodPressureDiastolic: dia, heartRate: hr, linkedCmpd: data.linkedCmpd || null }, ...(stored.checkIns || [])] };
    if (w != null) {
      const weightKg = weightUnit === 'lbs' ? lbsToKg(w) : w;
      patch.userWeight = String(weightKg);
      setUserWeight(String(weightKg));
    }
    upd(patch);
    posthog.capture('check_in_saved', { mood: data.mood ?? null });
    setCheckIn(false); setPostLogCheckIn(null);
    showToast(`${TRACKER_MOOD_LEVELS.find(m => m.value === data.mood)?.emoji || "✓"} Check in saved`);
  }, [stored, user, upd, setCheckIn, setPostLogCheckIn, setUserWeight, showToast, weightUnit, bloodGlucoseUnit]);

  return { saveCmpd, deleteCmpd, archiveCmpd, startEditCmpd, toggleZone, toggleHiddenCmpd, stepUpTitration, holdTitration, addCustomZone, removeCustomZone, updateCheckIn, deleteCheckIn, saveCheckIn, startBreak, endBreak };
}
